import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import { LiveAttachmentResponse } from '../entities/academy-responses.model';

/**
 * Support material for a live event, over the gated per-event actions
 * (`/LiveEvents/{id}/attachments`). Backend feature 012 made live material mirror
 * course material exactly, so this is the live twin of `CoursesService`'s
 * attachment trio; the binary lives in the external StorageService and is reached
 * through the `Url` the API returns.
 *
 * The generic `LiveAttachments` write endpoints were turned into `[NonAction]`
 * (405) and the OData model lost `Url`/`StorageObjectId`/`StorageKey`, so
 * `GET /odata/LiveAttachments` can no longer serve this screen — it only says that
 * material exists. Every read and write here goes through the actions below.
 */
@Injectable({ providedIn: 'root' })
export class LiveAttachmentsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `POST /LiveEvents/{id}/attachments` — multipart support material (field `file`).
   * The browser sets the multipart boundary; the interceptor skips `Content-Type`
   * for `FormData`. ⚠ 403 not producer · 404 missing / invisible event.
   */
  public addAttachment(
    liveEventId: string,
    file: File
  ): Observable<LiveAttachmentResponse> {
    const form = new FormData();
    form.append('file', file); // File carries its own name → multipart filename is preserved
    return this.http.post<LiveAttachmentResponse>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(
        liveEventId
      )}/attachments`,
      form,
      { withCredentials: true }
    );
  }

  /**
   * `GET /LiveEvents/{id}/attachments` — gated list of the event's support materials.
   * ⚠ 404 when the event does not exist, was deleted, or is invisible to the caller (the
   * visibility filter hides it outside the caller's app scope/audience) — since backend
   * 012-C this is the normal answer for a viewer outside the audience. ⚠ 403 only when the
   * event is visible but the caller is still refused (in practice a platform-tenant
   * non-staff user whose app/audience check fails). The producer always gets 200.
   * Audience is tenant-scoped, so no supervisor login can put the viewer inside it: marked
   * `withoutForbiddenElevation()` so that 403 never opens an elevation modal that cannot help.
   */
  public listAttachments(
    liveEventId: string
  ): Observable<LiveAttachmentResponse[]> {
    return this.http.get<LiveAttachmentResponse[]>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(
        liveEventId
      )}/attachments`,
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }

  /**
   * `DELETE /LiveEvents/{liveEventId}/attachments/{attachmentId}` — remove a support
   * material. Producer-only: a 403 here is the "not the producer" axis, which a
   * supervisor login CAN resolve, so it keeps the default elevation behaviour.
   * ⚠ 404 unknown attachment.
   */
  public removeAttachment(
    liveEventId: string,
    attachmentId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/LiveEvents/${encodeURIComponent(
        liveEventId
      )}/attachments/${encodeURIComponent(attachmentId)}`,
      { withCredentials: true }
    );
  }
}
